export interface SliderItemConfig {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  cta_text: string;
  cta_link: string;
  image_url: string;
  badge: string;
}

export interface FeatureItemConfig {
  icon: string;
  title: string;
  description: string;
}

export interface ContactInfoConfig {
  address: string;
  phone: string;
  email: string;
  working_hours: string;
  whatsapp: string;
  map_embed_url: string;
}

export interface SocialLinksConfig {
  instagram: string;
  facebook: string;
  youtube: string;
  twitter: string;
  whatsapp: string;
}

export interface FooterConfig {
  description: string;
  shop_links: { label: string; href: string }[];
  info_links: { label: string; href: string }[];
}

export interface NavLinkItem {
  label: string;
  href: string;
  isAuto?: boolean; // true = otomatik oluşan (Ürünler), label/href düzenlenemez
}

export interface NavbarConfig {
  links: NavLinkItem[];
}

export interface StoryConfig {
  badge: string;
  title: string;
  paragraph1: string;
  paragraph2: string;
  founded_year: string;
  image_url: string;
}

export interface NewsletterConfig {
  badge: string;
  title: string;
  description: string;
  cta_text: string;
  background_image: string;
}

export interface CampaignBannerConfig {
  main: { badge: string; title: string; description: string; cta_text: string; cta_link: string; image_url: string; };
  gift: { badge: string; title: string; description: string; cta_text: string; cta_link: string; image_url: string; };
  coldbrew: { badge: string; title: string; description: string; cta_text: string; cta_link: string; image_url: string; };
}

export interface GeneralConfig {
  site_name: string;
  logo_url: string;
  favicon_url: string;
  currency_symbol: string;
  free_shipping_threshold: number;
}

export interface TestimonialItemConfig {
  id: string;
  name: string;
  location: string;
  avatar_url: string;
  rating: number;
  text: string;
  product_name: string;
}

export interface TestimonialsConfig {
  badge: string;
  title: string;
  stats: { value: string; label: string }[];
  items: TestimonialItemConfig[];
}

// ─── Custom Pages ───────────────────────────────────────────────────────────

export interface PageSeoConfig {
  title: string;
  description: string;
  keywords: string;
  slug: string;
}

// ─── Block Types ─────────────────────────────────────────────────────────────

export type PageBlockType =
  | 'heading'
  | 'text'
  | 'image'
  | 'button'
  | 'divider'
  | 'spacer'
  | 'hero'
  | 'faq_list'
  | 'features_grid'
  | 'stats'
  | 'team';

export interface PageBlockHeading {
  type: 'heading';
  id: string;
  text: string;
  level: 'h1' | 'h2' | 'h3' | 'h4';
  align: 'left' | 'center' | 'right';
  color: string;
}

export interface PageBlockText {
  type: 'text';
  id: string;
  content: string;
  align: 'left' | 'center' | 'right';
  color: string;
}

export interface PageBlockImage {
  type: 'image';
  id: string;
  image_url: string;
  alt: string;
  caption: string;
  border_radius: number;
}

export interface PageBlockButton {
  type: 'button';
  id: string;
  label: string;
  href: string;
  variant: 'primary' | 'secondary' | 'outline';
  align: 'left' | 'center' | 'right';
  size: 'sm' | 'md' | 'lg';
}

export interface PageBlockDivider {
  type: 'divider';
  id: string;
  color: string;
}

export interface PageBlockSpacer {
  type: 'spacer';
  id: string;
  height: number;
}

export interface PageBlockHero {
  type: 'hero';
  id: string;
  title: string;
  subtitle: string;
  description: string;
  cta_text: string;
  cta_link: string;
  image_url: string;
  overlay_opacity: number;
}

export interface FaqItem {
  id: string;
  question: string;
  answer: string;
}

export interface PageBlockFaqList {
  type: 'faq_list';
  id: string;
  title: string;
  items: FaqItem[];
}

export interface FeatureGridItem {
  id: string;
  icon: string;
  title: string;
  description: string;
}

export interface PageBlockFeaturesGrid {
  type: 'features_grid';
  id: string;
  title: string;
  items: FeatureGridItem[];
}

export interface StatItem {
  id: string;
  value: string;
  label: string;
}

export interface PageBlockStats {
  type: 'stats';
  id: string;
  title: string;
  items: StatItem[];
}

export interface TeamMember {
  id: string;
  name: string;
  role: string;
  bio: string;
  image_url: string;
}

export interface PageBlockTeam {
  type: 'team';
  id: string;
  title: string;
  members: TeamMember[];
}

export type PageBlock =
  | PageBlockHeading
  | PageBlockText
  | PageBlockImage
  | PageBlockButton
  | PageBlockDivider
  | PageBlockSpacer
  | PageBlockHero
  | PageBlockFaqList
  | PageBlockFeaturesGrid
  | PageBlockStats
  | PageBlockTeam;

// ─── Column Layout ────────────────────────────────────────────────────────────

// width as percentage string: '100', '50', '33', '67', '25', '75', '30', '70'
export type ColumnWidth = '100' | '50' | '33' | '67' | '25' | '75' | '30' | '70';

export interface PageColumn {
  id: string;
  width: ColumnWidth; // percentage
  blocks: PageBlock[];
}

// ─── Section Layout ───────────────────────────────────────────────────────────

export type SectionLayout =
  | '1col'        // 100%
  | '2col-equal'  // 50/50
  | '2col-7030'   // 70/30
  | '2col-3070'   // 30/70
  | '2col-6733'   // 67/33
  | '2col-3367'   // 33/67
  | '3col-equal'  // 33/33/33
  | '4col-equal'; // 25/25/25/25

export interface PageSection {
  id: string;
  layout: SectionLayout;
  background_color: string;
  padding_y: 'none' | 'sm' | 'md' | 'lg' | 'xl';
  columns: PageColumn[];
}

export interface CustomPage {
  id: string;
  name: string;
  seo: PageSeoConfig;
  sections: PageSection[];
  created_at: string;
  updated_at: string;
}

// ─── Layout Definitions ───────────────────────────────────────────────────────

export interface SectionLayoutDef {
  id: SectionLayout;
  label: string;
  description: string;
  columns: ColumnWidth[];
  preview: string; // visual representation
}

export const SECTION_LAYOUTS: SectionLayoutDef[] = [
  { id: '1col', label: 'Tek Sütun', description: '100%', columns: ['100'], preview: '|████████████|' },
  { id: '2col-equal', label: 'İki Eşit Sütun', description: '50% / 50%', columns: ['50', '50'], preview: '|██████|██████|' },
  { id: '2col-7030', label: 'Geniş Sol', description: '70% / 30%', columns: ['70', '30'], preview: '|████████|████|' },
  { id: '2col-3070', label: 'Geniş Sağ', description: '30% / 70%', columns: ['30', '70'], preview: '|████|████████|' },
  { id: '2col-6733', label: '2/3 + 1/3', description: '67% / 33%', columns: ['67', '33'], preview: '|████████|████|' },
  { id: '2col-3367', label: '1/3 + 2/3', description: '33% / 67%', columns: ['33', '67'], preview: '|████|████████|' },
  { id: '3col-equal', label: 'Üç Eşit Sütun', description: '33% / 33% / 33%', columns: ['33', '33', '33'], preview: '|████|████|████|' },
  { id: '4col-equal', label: 'Dört Eşit Sütun', description: '25% / 25% / 25% / 25%', columns: ['25', '25', '25', '25'], preview: '|███|███|███|███|' },
];

export interface ThemeConfig {
  general: GeneralConfig;
  slider: SliderItemConfig[];
  features: FeatureItemConfig[];
  contact: ContactInfoConfig;
  social: SocialLinksConfig;
  footer: FooterConfig;
  navbar: NavbarConfig;
  story: StoryConfig;
  newsletter: NewsletterConfig;
  campaign: CampaignBannerConfig;
  testimonials: TestimonialsConfig;
  pages: CustomPage[];
}

// ─── Supabase + localStorage hybrid ─────────────────────────────────────────
// localStorage → anlık reaktivite (aynı sekme/tab)
// Supabase     → kalıcı depolama (canlı site dahil her yerden erişim)

const LS_KEY = 'zesha_theme_v1';
export const THEME_CHANGE_EVENT = 'zesha_theme_changed';
const SITE_ID = '8bff73b9-c558-4f08-ada1-8b0cc79e4c4b';

export function saveThemeConfig(config: ThemeConfig): void {
  try {
    // 1. localStorage'a yaz (anlık reaktivite)
    localStorage.setItem(LS_KEY, JSON.stringify(config));
    window.dispatchEvent(new CustomEvent(THEME_CHANGE_EVENT, { detail: config }));
  } catch { /* ignore */ }

  // 2. Supabase'e yaz (arka planda, kalıcı)
  import('@/lib/supabase').then(({ supabase }) => {
    supabase
      .from('siteler')
      .update({ tema_config: config, guncelleme_tarihi: new Date().toISOString() })
      .eq('id', SITE_ID)
      .then(({ error }) => {
        if (error) console.error('Tema Supabase kayıt hatası:', error.message);
      });
  });
}

export function loadThemeConfig(): ThemeConfig | null {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as ThemeConfig;
  } catch {
    return null;
  }
}

// Supabase'den tema yükle (sayfa ilk açıldığında çağrılır)
export async function loadThemeConfigFromDB(): Promise<ThemeConfig | null> {
  try {
    const { supabase } = await import('@/lib/supabase');
    const { data, error } = await supabase
      .from('siteler')
      .select('tema_config')
      .eq('id', SITE_ID)
      .maybeSingle();
    if (error || !data?.tema_config) return null;
    return data.tema_config as ThemeConfig;
  } catch {
    return null;
  }
}

export const defaultTestimonialsConfig: TestimonialsConfig = {
  badge: 'Müşterilerimiz',
  title: 'Binlerce Kahveseverin\nTercihi',
  stats: [
    { value: '12.400+', label: 'Mutlu Müşteri' },
    { value: '4.9/5', label: 'Ortalama Puan' },
    { value: '98%', label: 'Memnuniyet Oranı' },
    { value: '36+', label: 'Farklı Origin' },
  ],
  items: [
    {
      id: '1',
      name: 'Ayşe Kılıç',
      location: 'İstanbul',
      avatar_url: 'https://public.readdy.ai/ai/img_res/2a9f2a2f5670f7c4eb751dfdd9bfad86.jpg',
      rating: 5,
      text: 'Zesha\'yı keşfettiğimden beri başka hiçbir yerden kahve almıyorum. Etiyopya Yirgacheffe\'nin çiçeksi aroması sabah ritüelimi tamamen değiştirdi.',
      product_name: 'Etiyopya Yirgacheffe',
    },
    {
      id: '2',
      name: 'Mert Öztürk',
      location: 'Ankara',
      avatar_url: 'https://readdy.ai/api/search-image?query=professional%20portrait%20man%20forties%20smiling%20natural%20light%20minimal%20background%20warm%20tones%20confident%20expression%20casual&width=200&height=200&seq=ava2&orientation=squarish',
      rating: 5,
      text: 'Başlangıç Seti\'ni hediye olarak aldım, ama sonunda ben bayıldım. 3 farklı origin ile tanışmak harika bir deneyimdi.',
      product_name: 'Başlangıç Seti',
    },
    {
      id: '3',
      name: 'Selin Arslan',
      location: 'İzmir',
      avatar_url: 'https://readdy.ai/api/search-image?query=professional%20portrait%20young%20woman%20twenties%20smiling%20natural%20light%20minimal%20studio%20background%20warm%20tones%20happy%20expression&width=200&height=200&seq=ava3&orientation=squarish',
      rating: 5,
      text: 'Cold Brew Blend ile artık kendi cold brew\'umu yapıyorum. Zesha sadece kahve satmıyor, bir kahve kültürü sunuyor.',
      product_name: 'Cold Brew Blend',
    },
  ],
};

export const defaultThemeConfig: ThemeConfig = {
  general: {
    site_name: 'Zesha Coffee',
    logo_url: 'https://static.readdy.ai/image/9bc2349a8039d11e997391420dde935f/075f0886dd1f7b0625a50d721d43f74a.png',
    favicon_url: '',
    currency_symbol: '₺',
    free_shipping_threshold: 500,
  },
  slider: [
    {
      id: '1',
      title: 'Kahvenin En Saf\nHali',
      subtitle: 'Yeni Sezon Arrivals',
      description: 'Dünyanın en özel kahve bölgelerinden, hasat sonrası 72 saat içinde kavrulan single origin kahveler şimdi kapınızda.',
      cta_text: 'Koleksiyonu Keşfet',
      cta_link: '/urunler',
      image_url: 'https://readdy.ai/api/search-image?query=premium%20specialty%20coffee%20bags%20dark%20moody%20background%20dramatic%20lighting%20artisan%20roastery%20aesthetic%20elegant%20minimal%20multiple%20coffee%20bags%20displayed%20professional%20commercial%20photography%20warm%20tones&width=1920&height=900&seq=slider1&orientation=landscape',
      badge: '2026 Yeni Sezon',
    },
    {
      id: '2',
      title: 'Etiyopya\'nın\nGizemi',
      subtitle: 'Single Origin Seçkisi',
      description: 'Yirgacheffe\'nin 2.200m yüksekliğinde yetişen, yasemin çiçeği ve bergamot notalarıyla dolu efsanevi kahvesi.',
      cta_text: 'Ürünü İncele',
      cta_link: '/urun/etiyopya-yirgacheffe-filtre',
      image_url: 'https://readdy.ai/api/search-image?query=Ethiopia%20coffee%20farm%20high%20altitude%20green%20lush%20mountains%20coffee%20plants%20berries%20red%20specialty%20coffee%20origin%20story%20professional%20photography%20cinematic%20warm%20golden%20light&width=1920&height=900&seq=slider2&orientation=landscape',
      badge: 'Öne Çıkan',
    },
    {
      id: '3',
      title: 'Hediye Setleri\nBurada',
      subtitle: 'Özel Seremoniler İçin',
      description: '3 farklı single origin ile hazırlanmış premium hediye setleri — kahve tutkunları için en anlamlı sürpriz.',
      cta_text: 'Setleri Gör',
      cta_link: '/urunler?kategori=set-hediye',
      image_url: 'https://readdy.ai/api/search-image?query=luxury%20coffee%20gift%20set%20premium%20packaging%20elegant%20kraft%20boxes%20multiple%20coffee%20bags%20ribbon%20minimal%20white%20background%20product%20photography%20professional%20commercial&width=1920&height=900&seq=slider3&orientation=landscape',
      badge: 'Hediye Fikirleri',
    },
  ],
  features: [
    { icon: 'ri-truck-line', title: '500₺ Üzeri Ücretsiz Kargo', description: '500₺ ve üzeri siparişlerinizde ücretsiz standart kargo ile kapınıza teslim.' },
    { icon: 'ri-leaf-line', title: 'Hasat Sonrası Kavrum', description: 'Kahvelerimiz hasat sonrası 72 saat içinde kavrulur ve 24 saat içinde paketlenir.' },
    { icon: 'ri-award-line', title: 'Specialty Kalite Garantisi', description: 'SCA standartlarında puanlanan, 80+ puan specialty kahveler seçiyoruz.' },
    { icon: 'ri-refresh-line', title: '14 Gün İade Garantisi', description: 'Memnun kalmazsanız hiç sormadan 14 gün içinde iade alıyoruz.' },
  ],
  contact: {
    address: 'Bağcılar Mah. Kahve Sok. No:5\nBağcılar, İstanbul 34200',
    phone: '+90 500 123 45 67',
    email: 'merhaba@zesha.com.tr',
    working_hours: 'Pazartesi – Cuma: 09:00 – 18:00\nHafta sonu: Kapalı',
    whatsapp: '905001234567',
    map_embed_url: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3010.2!2d28.856!3d41.044!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDHCsDAyJzM4LjQiTiAyOMKwNTEnMjEuNiJF!5e0!3m2!1str!2str!4v1',
  },
  social: {
    instagram: 'https://instagram.com',
    facebook: 'https://facebook.com',
    youtube: 'https://youtube.com',
    twitter: 'https://twitter.com',
    whatsapp: '905001234567',
  },
  footer: {
    description: 'Dünyanın en özel kahve bölgelerinden, güneşin dokunduğu her mevsim hasat edilen single origin kahveleri sizinle buluşturuyoruz.',
    shop_links: [
      { label: 'Filtre Kahve', href: '/urunler?kategori=Filtre Kahve' },
      { label: 'Espresso', href: '/urunler?kategori=Espresso' },
      { label: 'Cold Brew', href: '/urunler?kategori=Cold Brew' },
      { label: 'Set & Hediye', href: '/urunler?kategori=Set & Hediye' },
      { label: 'Tüm Ürünler', href: '/urunler' },
    ],
    info_links: [
      { label: 'Hakkımızda', href: '/hakkimizda' },
      { label: 'Blog', href: '/blog' },
      { label: 'SSS', href: '/sss' },
      { label: 'İletişim', href: '/iletisim' },
      { label: 'Kargo Bilgisi', href: '/kargo' },
    ],
  },
  navbar: {
    links: [
      { label: 'Ana Sayfa', href: '/' },
      { label: 'Ürünler', href: '/urunler', isAuto: true },
      { label: 'İletişim', href: '/iletisim' },
    ],
  },
  story: {
    badge: 'Hikayemiz',
    title: 'Kahveye Duyduğumuz\nTutkudan Doğdu',
    paragraph1: 'Zesha, 2019 yılında İstanbul\'da bir grup kahve tutkunuyla başladı. Hayalimiz basitti: Dünyanın en iyi kahve bölgelerinden, çiftçilerle doğrudan çalışarak getirdiğimiz kahveleri sana en taze haliyle sunmak.',
    paragraph2: 'Her çuval kahveyi bizzat test ediyor, SCA eğitimli ekibimizle kavruyoruz. Hasat sonrası 72 saat içinde kavrum, 24 saat içinde paketleme — çünkü tazelik bizim için bir tercih değil, bir standart.',
    founded_year: '2019',
    image_url: 'https://readdy.ai/api/search-image?query=coffee%20roasting%20artisan%20roastery%20craft%20roaster%20professional%20specialty%20coffee%20roasting%20process%20warm%20lighting%20dark%20background%20professional%20photography&width=700&height=700&seq=story1&orientation=squarish',
  },
  newsletter: {
    badge: 'Özel Fırsatlar',
    title: 'Kahve Dünyamıza Katıl',
    description: 'İlk siparişinde %10 indirim kazan. Yeni ürün, kampanya ve kahve hikayeleri doğrudan e-postana gelsin.',
    cta_text: 'Üye Ol, %10 İndirim Al',
    background_image: 'https://readdy.ai/api/search-image?query=coffee%20beans%20abstract%20texture%20macro%20photography%20dark%20moody%20background%20dramatic%20lighting%20bokeh%20specialty%20coffee%20roastery%20aesthetic&width=1400&height=600&seq=cta1&orientation=landscape',
  },
  testimonials: defaultTestimonialsConfig,
  pages: [],
  campaign: {
    main: {
      badge: 'Sınırlı Süre',
      title: '3 Al, 2 Öde',
      description: 'Tüm filtre kahvelerimizde geçerli. En pahalı ürün hediye!',
      cta_text: 'Kampanyayı Keşfet',
      cta_link: '/urunler?kategori=Filtre Kahve',
      image_url: 'https://readdy.ai/api/search-image?query=premium%20coffee%20bags%20dark%20moody%20flat%20lay%20photography%20multiple%20craft%20paper%20bags%20coffee%20beans%20scattered%20elegant%20minimal%20dark%20background%20artisan%20specialty%20roastery&width=800&height=500&seq=camp1&orientation=landscape',
    },
    gift: {
      badge: 'Özel Setler',
      title: 'Sevdiklerine\nÖzel Hediye',
      description: 'Premium hediye paketleme dahil',
      cta_text: 'Setleri İncele',
      cta_link: '/urunler?kategori=Set & Hediye',
      image_url: 'https://readdy.ai/api/search-image?query=luxury%20premium%20coffee%20gift%20box%20elegant%20packaging%20ribbon%20bow%20white%20background%20product%20photography%20professional%20warm%20tones&width=700&height=400&seq=camp2&orientation=landscape',
    },
    coldbrew: {
      badge: 'Yeni Geldi',
      title: 'Cold Brew\nKoleksiyonu',
      description: '12 saat soğuk demleme için',
      cta_text: 'Keşfet',
      cta_link: '/urunler?kategori=Cold Brew',
      image_url: 'https://readdy.ai/api/search-image?query=cold%20brew%20coffee%20glass%20ice%20cubes%20minimal%20dark%20navy%20background%20product%20photography%20specialty%20coffee%20refreshing%20professional%20commercial&width=700&height=400&seq=camp3&orientation=landscape',
    },
  },
};

export function getMergedTheme(): ThemeConfig {
  const saved = loadThemeConfig();
  if (!saved) return defaultThemeConfig;
  return deepMerge(defaultThemeConfig, saved) as ThemeConfig;
}

function deepMerge(target: unknown, source: unknown): unknown {
  if (typeof target !== 'object' || target === null) return source;
  if (typeof source !== 'object' || source === null) return source;
  if (Array.isArray(source)) return source;
  const result = { ...(target as Record<string, unknown>) };
  for (const key of Object.keys(source as Record<string, unknown>)) {
    const sv = (source as Record<string, unknown>)[key];
    const tv = (target as Record<string, unknown>)[key];
    result[key] = deepMerge(tv, sv);
  }
  return result;
}
